import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';

import { FeaturePermission } from '../../constants/feature.permission.helper';
import { FilterTypeEnum } from '../../enuns/filterType.enum';
import { normalizeStoreTypeSaleMarketValue } from '../../models/StoreTypeSaleMarket';
import store from '../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';
import api from '../api';
import DataTable from '../core/charts/Datatable';
import dataBoard from '../core/databoard';
import filters from '../core/filters';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import ColorBrewer from '../core/tools/colorbrewer';
import translate from '../core/tools/translate';
import environment from '../environment';
import StoresLayer from "../layers/StoresLayer";
import { openTagStyleEditor } from '../core/dialog/tag-style-editor';
import { clearTagStyles } from '../layers/Leaflet/VectorLayer/TagStyleStorage';
import Workspace from '../workspaces';
import { ControlType, Item } from '../workspaces/item';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import type { Period } from './types/control-period.type';
import { StoreExportTypeEnum } from '../../enuns/store.enun';
import { VehicleTypeEnum } from './../core/vehicleType/constants';

const LayerInfo = (name) => require('../info').get(name);

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

const GetLayer = (name, currentMap = worldMap.getCurrent()) => {
    return require('../layers/index').default.get(name, currentMap);
};

export class StoresControl extends CoreControl {
    period: Period;
    _dictPermissionShowVmm = {
        'stores': FeaturePermission.LAYER_NETWORK.showVmm,
        'competitors': FeaturePermission.LAYER_COMPETITORS.showVmm,
        'storesMotocycle': FeaturePermission.LAYER_STORES_MOTOCYCLE.showVmm,
    };
    vmmChecked = false;

    constructor(options) {
        super(options);

        this._change = this._change.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this._toggleLabel = this._toggleLabel.bind(this);
        this._toggleSales = this._toggleSales.bind(this);
        this._toggleProtectArea = this._toggleProtectArea.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.highlight = this.highlight.bind(this);
        this.unselect = this.unselect.bind(this);
        this.select = this.select.bind(this);
        this.redraw = this.redraw.bind(this);
        this.resetLayer = this.resetLayer.bind(this);
        this.resizeIcon = this.resizeIcon.bind(this);
        this.show = this.show.bind(this);
        this.toggleClientStores = this.toggleClientStores.bind(this);
        this.toggleReported = this.toggleReported.bind(this);
        this.toggleVisibility = this.toggleVisibility.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);

        this._instance = null;
        this.initPeriod();
    }

    get isStoreInactive() {
        return this.options.name === 'StoreInactive';
    }

    get isStoresMotocycle() {
        return this.options.name === 'StoresMotocycle';
    }

    get isStoresConsortium() {
        return this.options.name === 'StoresConsortium';
    }

    get isStoreUnapproved() {
        return this.options.name === 'UnapprovedStores';
    }

    get isStorePlanned() {
        return this.options.name === 'StorePlanned';
    }

    get smallestPinsPermission() {
        return {
            Dealerships: FeaturePermission.LAYER_NETWORK.showSmallestPins,
            Competitors: FeaturePermission.LAYER_COMPETITORS.showSmallestPins
        }[this.options.name];
    }

    get smallestPinIcon() {
        return {
            Dealerships: 'pin_concessionarias_sml.png',
            Competitors: 'pin_concorrentes_sml.png'
        }[this.options.name];
    }

    isSmallestPinSelected(mapId) {
        const pinSize = (this.getControls(mapId).radio || {})[`pinSize${this.options.name}`] || {};
        return pinSize.value === 'smallest';
    }

    init() {
        const values = {
            id: `layer-${this.options.name.toLowerCase()}`,
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            help: translate(`Help.${this.options.name}`),
            layer: this.getLayer,
            name: this.options.name,
            type: 'vector-points',
            purge: this.resetLayer,
            overview: isAuthorized(FeaturePermission.LAYER_NETWORK.showDataPanel) && !this.isStoreInactive && !this.isStorePlanned && !this.isStoreUnapproved && !this.isStoresMotocycle && !this.isStoresConsortium ? this.toggleOverview : false,
            filter: {
                enable: true,
                click: this.createFilter
            },
            change: this._change,
            resize: this.isStoreInactive ? false : {
                name: `pinSize${this.options.name}`,
                change: this.resizeIcon,
                type: 'radio'
            },
            smallestPoint: !!this.smallestPinsPermission && isAuthorized(this.smallestPinsPermission),
            tab: () => {
                return $(`#${this.options.name.toLowerCase()}-tab-link`);
            },
            dropdown: [],
            checkbox: {}
        };

        // Dealers have customers
        if (isAuthorized(FeaturePermission.LAYER_CUSTOMER.enable)) {
            values.switch = { deactivate: ['Customers'] };
        }

        if (_.size(environment.get('CONFIG.FILTERS.CUSTOMERS_STORES')) && this.options.name !== 'Competitors') {
            values.checkbox[translate('Action.FilterMyStores')] = {
                name: 'clientStoresFlag',
                change: this.toggleClientStores,
                value: ''
            };
        }

        if (isAuthorized('stores-quarantine-new')) {
            values.checkbox[translate('Action.ShowReportedStores')] = {
                name: 'reported',
                change: this.toggleReported,
                value: ''
            };
        }

        this._tags = {
            tagName: {
                property: 'Nome',
                align: 'east',
                size: 9,
                offsetY: 20,
                offsetX: -2,
                bold: true,
                box: true,
            },
            tagValue: {
                property: 'MediaMensal',
                align: 'west',
                size: 11,
                offsetY: 20,
                offsetX: -2,
                bold: true,
                box: true,
                mask: '0,0'
            },
            tagNameGroup: {
                property: 'Grupo',
                align: 'east',
                size: 9,
                offsetX: -2,
                bold: true,
                box: true,
            },
            tagNameGroupClient: {
                property: 'GrupoCliente',
                align: 'east',
                size: 9,
                offsetX: -2,
                offsetY: -20,
                bold: true,
                box: true,
            }
        };

        if (isAuthorized(FeaturePermission[this.options.context].showVmm) && !this.isStoreInactive && !this.isStorePlanned) {
            const formatDate = (date: Date): string => moment(date).format('MMM/YYYY');
            const { start, end } = this.period;

            values.checkbox[translate('Action.ShowAverageSalesAlt')] = {
                name: `tagValue`,
                change: this._toggleSales,
                link: {
                    id: 'layer-stores-date',
                    name: 'StoresByYear',
                    text: `${formatDate(start)} -> ${formatDate(end)}`,
                    value: this.valueForDatePicker,
                    change: async (checked, data) => {
                        await this.changePeriod(data);
                    },
                    type: 'datepicker'
                },
                style: {
                    change: (tag) => {
                        this._tags.tagValue = tag;
                        this._toggleSales(this.getControls().checkbox.tagValue.status);
                    },
                    clearTagStyles: () => {
                        clearTagStyles(this._getTagStyleScope(), 'MediaMensal');
                        this._reloadIfNeeded();
                    },
                    options: this._tags.tagValue
                }
            };
        }

        values.checkbox[translate('Action.ShowStoresName')] = {
            name: `tagName`,
            change: this._toggleLabel,
            style: {
                change: async (tag) => {
                    this._tags.tagName = tag;
                    await this._toggleLabel(this.getControls().checkbox.tagName.status);
                },
                clearTagStyles: () => {
                    clearTagStyles(this._getTagStyleScope(), 'Nome');
                    this._reloadIfNeeded();
                },
                options: this._tags.tagName
            }
        };

        if (isAuthorized(FeaturePermission.LAYER_UNAPPROVED_STORES.enable) && this.options.name === 'UnapprovedStores'
            || isAuthorized(FeaturePermission.LAYER_NETWORK.showGroup) && this.options.name === 'Dealerships'
            || isAuthorized(FeaturePermission.LAYER_STORES_CONSORTIUM.showGroup) && this.isStoresConsortium) {
            values.checkbox[`${translate('Action.ShowTagName')} ${translate('Common.Group')}`] = {
                name: 'tagNameGroup',
                change: this._toggleLabel,
                style: {
                    change: async (tag) => {
                        this._tags.tagNameGroup = tag;
                        await this._toggleLabel(this.getControls().checkbox.tagNameGroup.status);
                    },
                    options: this._tags.tagNameGroup
                },
            };
        };

        if (isAuthorized(FeaturePermission.LAYER_NETWORK.showGroupClient) && this.options.name === 'Dealerships') {
            values.checkbox[`${translate('Action.ShowTagName')} ${translate('Common.Group')} ${translate('Common.Client')}}`] = {
                name: 'tagNameGroupClient',
                change: this._toggleLabel,
                style: {
                    change: async (tag) => {
                        this._tags.tagNameGroupClient = tag;
                        await this._toggleLabel(this.getControls().checkbox.tagNameGroupClient.status);
                    },
                    options: this._tags.tagNameGroupClient
                },
            };
        };

        if (isAuthorized(FeaturePermission.LAYER_UNAPPROVED_STORES.showProtectArea) && this.options.name === 'UnapprovedStores') {
            values.checkbox[translate('Action.ShowProtectArea')] = {
                name: `protectAreas`,
                change: this._toggleProtectArea,
            };
        }

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    get valueForDatePicker() {
        if (this.isStoresMotocycle) {
            const end = environment.get('CONFIG.FILTERS.ENDYEAR');
            const start = new Date(end.getFullYear(), 0, 1);
            start.setMonth(start.getMonth() - 11);

            return {
                ...this.period,
                max: end,
                min: start,
            };
        }

        return {
            ...this.period,
            max: environment.get('CONFIG.FILTERS.ENDYEAR'),
            min: environment.get('CONFIG.FILTERS.STARTYEAR'),
        };
    }

    async _change(e) {
        await super._change(e);
        this._setFilterButtonVisibility(e.checked);
        Workspace.addAction(new Item(`#${$(e).prop('id')}`, e.checked, ControlType.ICheckBox));

        if (this.getControls().checkbox.protectAreas) {
            this._toggleProtectArea(this.getControls().checkbox.protectAreas.status);
        }
    }

    _getTagPropertyValue(property) {
        if (property === 'value')
            return _.assign({}, this._tags.tagValue);

        if (property === 'nameGroup') {
            return _.assign({}, this._tags.tagNameGroup);
        }

        if (property === 'nameGroupClient') {
            return _.assign({}, this._tags.tagNameGroupClient);
        }

        return _.assign({}, this._tags.tagName);
    }

    _getLayerTag() {
        const value = this._getTagPropertyValue('value');
        const name = this._getTagPropertyValue('name');
        const nameGroup = this._getTagPropertyValue('nameGroup');
        const nameGroupClient = this._getTagPropertyValue('nameGroupClient');

        const result = [];
        const controls = this.getControls();

        if (controls.checkbox) {
            const { checkbox } = controls;
            if (checkbox.tagValue && checkbox.tagValue.status) {
                result.push(value);
            }
            if (checkbox.tagName && checkbox.tagName.status) {
                result.push(name);
            }
            if (checkbox.tagNameGroup && checkbox.tagNameGroup.status) {
                result.push(nameGroup);
            }
            if (checkbox.tagNameGroupClient && checkbox.tagNameGroupClient.status) {
                result.push(nameGroupClient);
            }
        }

        if (result.length > 0) {
            return result;
        } else {
            return null;
        }
    }

    _getTagStyleScope() {
        return {
            client: environment.get('CONFIG.CLIENT'),
            layerName: this.options.name,
            areaType: 'default',
            username: sessionStorage.username
        };
    }

    // Options that enable clicking a store's name tag to edit (and persist
    // locally) only that store's tag style. Consumed by TagsLayer.
    _tagStyleLayerOptions() {
        return {
            tagEditable: true,
            tagStyleProperty: ['Nome', 'MediaMensal'],
            tagStyleScope: this._getTagStyleScope(),
            onTagEdit: (feature, baseOptions) => openTagStyleEditor({
                title: translate('Common.Name'),
                baseOptions,
                feature,
                property: baseOptions.property,
                scope: this._getTagStyleScope(),
                onSaved: () => this._reloadIfNeeded()
            })
        };
    }

    _setLabel() {
        this.getLayer().setOptions(_.assign({ tag: this._getLayerTag() }, this._tagStyleLayerOptions()));
    }

    async _toggleLabel() {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Show Label', '');
        this._setLabel();
        if (this.getLayer().visible()) {
            await this.getLayer().reload();
        }
    }

    async _toggleSales(checked) {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const { symbology } = layer.options;

        api.general.log.action(environment._getContextEnum(this.options.name), 'Show VMM', '');

        if (!this.filterOptions) {
            this.filterOptions = {};
        }

        this.vmmChecked = checked;

        if (checked) {
            await this.addSalesProperty();
            symbology.heat.theme = ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_BLUE);
            symbology.heat.property = 'MediaMensal';
            this.filterOptions[`showVMM${this.options.name}`] = true;
        } else {
            symbology.heat.theme = ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_GREEN);
            symbology.heat.property = undefined;
            this.filterOptions[`showVMM${this.options.name}`] = false;
        }

        this._setFilterButtonVisibility(true);
        filters.updateControls(this.filterOptions);
        filters.reloadFilters(worldMap.getCurrent()._id, this.filterOptions);
        this.getLayer().setOptions({ symbology, optFilters: this.filterOptions });
        await this._reloadIfNeeded();
    }

    async changePeriod(data) {
        this.period = data;
        api.general.log.action(environment._getContextEnum(this.options.name), 'Mudou Período', JSON.stringify(this.period));

        if (this.vmmChecked) {
            await this._toggleSales(this.vmmChecked);
        }

        await this._reloadIfNeeded();
    }

    async _reloadIfNeeded() {
        this._setLabel();
        if (this.check() && this.getLayer().visible()) {
            await this.getLayer().reload();
        }
    }

    initPeriod() {
        const end = environment.get('CONFIG.FILTERS.ENDYEAR');
        let start = new Date(end.getFullYear(), 0, 1);

        if (this.isStoresMotocycle) {
            start = new Date(end.getFullYear() - 1, 0, 1);
        }

        this.period = { start, end };
    }

    get currentPeriod() {
        const { start, end } = this.period;

        const formatDate = (dt: Date) => moment(dt).format("MM/YYYY");
        return { start: formatDate(start), end: formatDate(end) };
    }

    get layerPeriod() {
        return this.period;
    }

    async toggleOverview(e) {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);

        const showOverview = () => {
            const features = layer.getCurrentFeatures();

            if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))
                api.general.log.action(environment._getContextEnum(this.options.name), 'Visualizar Gráfico');

            if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
                this.summary(features);
                this._overviewFeatures = _.cloneDeep(features);

                store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
            }
        };

        if (isAuthorized(FeaturePermission[this.options.context].showVmm)) {
            const fc = await this.addSalesProperty();
            layer.setFeatures(fc);
            showOverview();
        } else {
            showOverview();
        }
    }

    _view(values) {
        return LayerInfo('Stores').create(values, this.options.name);
    }

    _toArray(t) {
        if (/, /.test(String(t))) {
            return String(t).split(', ');
        } else {
            return String(t).split(',');
        }
    }

    _getBrandFilterValues(values) {
        const brands = values.brands;

        if (!brands || !_.isArray(brands.text) || !_.size(brands.text)) {
            return [];
        }

        const isAutoFilled = !!(brands.metadata || {}).all;
        const hasClassification = !!(values.classification && _.size(values.classification.value));

        return isAutoFilled && !hasClassification ? [] : brands.text;
    }

    async _filterCallback(values) {
        const group = values.group || values.groupsPlannedStore;

        const selectedFilters = {
            Bandeira: this._getBrandFilterValues(values),
            Grupo: (group && _.isArray(group.text) && _.size(group.text)) ? group.text : [],
            Tipo: (values.type && _.isArray(values.type.text) && _.size(values.type.text)) ? values.type.text : [],
            Segment: (values.segment && _.isArray(values.segment.value) && _.size(values.segment.value)) ? values.segment.value : [],
            FuelTypes: {
                type: FilterTypeEnum.Include,
                data: (values.fuelType && _.isArray(values.fuelType.value) && _.size(values.fuelType.value)) ? values.fuelType.value : []
            },
            UF: (values.state && _.isArray(values.state.text) && _.size(values.state.text)) ? values.state.text : [],
            NameAreaOperational: (values.areaOperational && _.isArray(values.areaOperational.text) && _.size(values.areaOperational.text)) ? values.areaOperational.text : [],
            TipoVendaMercado: (values.storeTypeSaleMarket && values.storeTypeSaleMarket.value) ? values.storeTypeSaleMarket.value : [],
            IdAttributes: {
                type: FilterTypeEnum.Include,
                data: (values.attributes && _.isArray(values.attributes.value) && _.size(values.attributes.value)) ? values.attributes.value : []
            },
            IdIndustry: values.industry && values.industry.value ? values.industry.value : [],
            TipoLojaNaoHomologadaId: values.typeStores && values.typeStores.value ? values.typeStores.value : [],
            GrupoCliente: (values.groupClient && _.isArray(values.groupClient.text) && _.size(values.groupClient.text)) ? values.groupClient.text : [],
        };

        selectedFilters.TipoVendaMercado = (_.isArray(selectedFilters.TipoVendaMercado)
            ? selectedFilters.TipoVendaMercado
            : [selectedFilters.TipoVendaMercado]
        ).map(x => normalizeStoreTypeSaleMarketValue(x));

        selectedFilters.IdIndustry = _.isArray(selectedFilters.IdIndustry)
            ? selectedFilters.IdIndustry.map(x => x.toString())
            : [selectedFilters.IdIndustry.toString()];

        const filter = this.getLayerFilters();

        _.forEach(selectedFilters, (val, key) => {
            if (_.size(val) || (_.isBoolean(val))) {
                filter.properties[key] = val;
            } else {
                delete filter.properties[key];
            }
        });

        await this.addSalesProperty(filter);
        await this.setLayerFilters(filter);
        api.general.log.action(environment._getContextEnum(this.options.name), 'Filtro', JSON.stringify(filter));

        if (!this.check()) {
            this.check(true);
        }
    }


    setDefaultFilters() {
        if (isAuthorized(FeaturePermission.LAYER_COMPETITORS.filterStoreAttribute) && environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto && this.options.name === 'Competitors') {
            const sourceProducts = environment.get('CONFIG.FILTERS.STORE_COMPETITORS_CONTEXT_ATTRIBUTES');
            const motoSourceProducts = sourceProducts.filter(x => x.attributeName === "Moto");
            const defaultFilters = {
                attributes: { "metadata": { "name": "attributes", "text": "attributeName", "value": "idAttributeType", "properties": ["idAttributeType", "attributeName"] }, "value": [motoSourceProducts[0].idAttributeType], "text": [motoSourceProducts[0].attributeName] }
            };
            filters.set(
                `${_.kebabCase(this.options.name)}-filter-id`,
                worldMap.getCurrent()._id,
                defaultFilters
            );
            this._filterCallback(defaultFilters);
        }
    }

    _setFilterButtonVisibility(state) {
        try {
            Object.keys(this.getControls().button).forEach(item => {
                if (this.getControls().button[item].input) {
                    this.getControls().button[item].input.enable(state);
                }
                this.getControls().button[item].enable = state;
            });

            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.error(err);
        }

        if (state) {
            this.createFilter();
        }

        if (!state) {
            filters.close();
            LayerInfo('Stores').remove();
        }
    }

    _toggleProtectArea(checked) {
        const layerName = this._getLayerName();
        const protectAreaLayer = this._getProtectedAreaLayer();
        const storeType = StoreExportTypeEnum.UnapprovedStores;
        if (checked && this.getControls().switch.onOff.status) {
            protectAreaLayer.onLoad(layerName, storeType);
        } else {
            protectAreaLayer.offLoad();
        }
    }

    _getProtectedAreaLayer() {
        const protectAreaLayer = GetLayer('ProtectAreaUnapprovedStores');
        return protectAreaLayer;
    }

    _getLayerName() {
        const layerName = "UnapprovedStores";
        return layerName;
    }


    summary(features) {
        const data = this.calculate(features);

        const chartPie = new DataTable(data.groups, {
            row: translate('Common.Group'),
            column: [translate('Common.Total')],
        });

        const chartColumn = new DataTable(data.sales, {
            row: 'Dealer',
            column: [translate('Common.Sales')],
            sort: translate('Common.Sales'),
        });

        const canSeeGroupClient = isAuthorized(FeaturePermission.LAYER_NETWORK.showGroupClient);
        let chartPieSecond = null;

        if (canSeeGroupClient && _.size(data.groupsClients) > 0) {
            chartPieSecond = new DataTable(data.groupsClients, {
                row: translate('Common.Group'),
                column: [translate('Common.Total')],
            });
        }

        const options = {
            title: {
                table: `${translate(`Common.${this.options.name}`)} - ${translate('Common.Summary')} | ${this.formattedPeriod()}`,
                chartPie: `RANKING ${translate('Common.Group')}`,
                chartColumn: `RANKING ${translate('Common.Sales')}`,
                ...(chartPieSecond && {
                    chartPieSecond: `RANKING ${translate('Common.Group')} ${translate('Common.Client')}`,
                }),
            },
            dash: true,
            nameFile: `${environment.get('CONFIG.CLIENT')}_Painel de Dados_${translate(`Common.${this.options.name}`)}_v${moment().format('YYYYMMDD')}`,
            data: [data.summary, chartPie, chartColumn, ...(chartPieSecond ? [chartPieSecond] : [])],
            fourthLevel: canSeeGroupClient && _.size(data.groupsClients) > 0,
            convertTable: false,
            columns: this.getColumnsFromSummary(),
            buttons: {
                fullscreen: true,
                export: isAuthorized(FeaturePermission[this.options.context].exportDataPanel),
            },
        };

        if (canSeeGroupClient) {
            options.columns.splice(2, 0, {
                field: 'groupClient',
                title: `${translate('Common.Group')} ${translate('Common.Client')}`
            });
        }

        dataBoard.show(options);
    }

    calculate(features) {
        const total = _.sumBy(features, f => f.properties.TotalVendas);

        const summary = _(features)
            .map(f => ({
                name: f.properties.Nome,
                brand: f.properties.Bandeira,
                dealerNumber: f.properties.IdLojaCliente,
                group: f.properties.Grupo,
                groupClient: f.properties.GrupoCliente,
                sales: f.properties.TotalVendas,
                percentage: f.properties.TotalVendas / total,
                monthlyAverage: f.properties.MediaMensal
            }))
            .orderBy('sales')
            .reverse()
            .value();

        const groups = _(features)
            .groupBy(f => f.properties.Grupo)
            .map((val, key) => ({
                [translate('Common.Group')]: key,
                [translate('Common.Total')]: val.length
            }))
            .uniqBy(translate('Common.Group'))
            .orderBy(translate('Common.Total'))
            .reverse()
            .value();

        const groupsClients = _(features)
            .groupBy(f => f.properties.GrupoCliente)
            .map((val, key) => ({
                [translate('Common.Group')]: key,
                [translate('Common.Total')]: val.length
            }))
            .uniqBy(translate('Common.Group'))
            .orderBy(translate('Common.Total'))
            .reverse()
            .value();

        let sales = _(features)
            .filter(f => f.properties.TotalVendas)
            .map((f) => ({
                'Dealer': `${f.properties.Bandeira} - ${f.properties.Nome}`,
                [translate('Common.Sales')]: f.properties.TotalVendas
            }))
            .orderBy([translate('Common.Sales'), 'Dealer'], ['desc', 'asc'])
            .take(42)
            .value();

        this._treatDealersWithSameName(sales);

        return {
            groups,
            groupsClients,
            sales,
            summary
        };
    }

    getColumnsFromSummary() {
        if (this.options.name === 'Dealerships') {
            return [
                {
                    field: 'name',
                    title: translate('Common.Name'),
                },
                {
                    field: 'dealerNumber',
                    title: translate('Common.DealerNumber'),
                },
                {
                    field: 'group',
                    title: translate('Common.Group'),
                },
                {
                    field: 'brand',
                    title: translate('Common.Brand'),
                    width: 80
                },
                {
                    field: 'monthlyAverage',
                    title: translate('Common.AverageSales'),
                    template: d => numeral(d.monthlyAverage).format('0,0[.]00'),
                    width: 70
                },
                {
                    field: 'sales',
                    title: translate('Common.TotalSales'),
                    template: d => numeral(d.sales).format('0,0[.]00'),
                    width: 70
                },
                {
                    field: 'percentage',
                    title: '(%)',
                    template: d => numeral(d.percentage).format('0,0[.]00%'),
                    width: 70
                },
            ];
        }

        return [
            {
                field: 'name',
                title: translate('Common.Name'),
            },
            {
                field: 'group',
                title: translate('Common.Group'),
            },
            {
                field: 'brand',
                title: translate('Common.Brand'),
                width: 80,
            },
            {
                field: 'monthlyAverage',
                title: translate('Common.AverageSales'),
                template: d => numeral(d.monthlyAverage).format('0,0[.]00'),
                width: 70,
            },
            {
                field: 'sales',
                title: translate('Common.TotalSales'),
                template: d => numeral(d.sales).format('0,0[.]00'),
                width: 70,
            },
            {
                field: 'percentage',
                title: '(%)',
                template: d => numeral(d.percentage).format('0,0[.]00%'),
                width: 70,
            },
        ];
    }

    createFilter() {
        this.filterOptions = {
            ...this.filterOptions,
            ... {
                id: `${_.kebabCase(this.options.name)}-filter-id`,
                label: translate(`Common.${this.options.name}`),
                type: _.lowerCase(this.options.name),
                onSubmit: this._filterCallback
            }
        };

        filters.create(this.filterOptions);

        this.setDefaultFilters();
    }

    getLayerFilters() {
        const filter = (this.getLayer().options.filter || {});

        if (!filter.properties) filter.properties = {};

        return filter;
    }

    async setLayerFilters(filter) {
        this.getLayer().setOptions({ filter });

        if (this.check() && this.getLayer().visible()) {
            await this.getLayer().reload();
        }
    }

    async toggleClientStores(checked) {
        const filter = this.getLayerFilters();

        if (checked) {
            filter.properties.IdLojaCliente = environment.get('CONFIG.FILTERS.CUSTOMERS_STORES');
        } else {
            delete filter.properties.IdLojaCliente;
        }

        await this.setLayerFilters(filter);
    }

    async resizeIcon(value) {
        const { id, options } = this.getLayer();
        const { symbology } = options;
        const imgSize = {
            sm: '_sm',
            xs: '_xs',
            sml: '_sml'
        };

        const sizes: { size: string, imgSuffix: string, iconSize: number[], iconAnchor: number[], labelAnchor: number[] }[] = [
            { size: 'large', imgSuffix: '', iconSize: [50, 64], iconAnchor: [25, 60], labelAnchor: [25, -32] },
            { size: 'medium', imgSuffix: imgSize.sm, iconSize: [38, 48], iconAnchor: [19, 48], labelAnchor: [19, -24] },
            { size: 'small', imgSuffix: imgSize.xs, iconSize: [28, 28], iconAnchor: [14, 14], labelAnchor: [14, 0] },
            { size: 'smallest', imgSuffix: imgSize.sml, iconSize: [14, 14], iconAnchor: [14, 14], labelAnchor: [14, 0] }
        ];

        const { imgSuffix, iconSize, iconAnchor, labelAnchor, size } = sizes.find(x => x.size === value);
        api.general.log.action(environment._getContextEnum(this.options.name), `Mudou Tam. Icone: ${size}`);

        symbology.style.icon = {
            getUrl: store => {

                const urlToSml = imgSuffix === imgSize.sml ? `${environment.get('IMAGES_HOST')}/${this.smallestPinIcon}` : undefined;

                if (urlToSml) {
                    return urlToSml;
                }

                const applyUrl = imgSuffix === imgSize.sm || imgSuffix === imgSize.xs
                    ? StoresLayer.getApplicatorUrlIconWithoutTypeStore(this.options.name)
                    : StoresLayer.getApplicatorUrlIcon(this.options.name);
                const url = applyUrl(store);
                return url.replace('.png', `${imgSuffix}.png`);
            },
            iconSize,
            iconAnchor,
            labelAnchor
        };

        this.getLayer().setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            await this.getLayer().reload();
        }
    }

    async addSalesProperty(filters = null, parameterIsDirectSales = null, filterPeriod = null) {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const type = isAuthorized('Direct-sales-filters-sale-type') && isAuthorized('Direct-sales-filters-channels');
        let fc = layer.toGeoJSON();

        if (!isAuthorized(FeaturePermission[this.options.context].showVmm)) {
            return fc;
        }

        const getId = f => Number(f.properties.IdOriginal);
        const { start, end } = filterPeriod || this.period;
        const getYearMonth = (date) => Number(moment(date).format('YYYYMM'));
        let f = (this.getLayer().options.filter || {});

        if (filters != null) {
            f = filters;
        }

        if (fc.features.length) {
            const data = {
                ids: fc.features.map(getId),
                anoMesInicio: getYearMonth(start),
                anoMesFim: getYearMonth(end),
                idSegments: f.properties ? f.properties.Segment : [],
                idIndustry: f.properties ? f.properties.IdIndustry : [],
                type: type,
                isStoresMotocycle: this.isStoresMotocycle,
            };

            const res = await api.store.get.sales(data);
            fc.features.forEach(f => {
                const values = res.filter(x => x.id === getId(f));

                f.properties.MediaMensal = Math.round(_(values).sumBy(x => x.media)) || 0;
                f.properties.TotalVendas = Math.round(_(values).sumBy(x => x.total)) || 0;
                f.properties.Segment = values.map(x => x.codigoSegmento);
            });
        }

        return fc;
    }

    toggleReported(checked) {
        const { params } = this.getLayer().options.remote;
        params.LoadReportedStores = checked;
        this.getLayer().setOptions({
            remote: {
                params
            }
        });
        if (this.check() && this.getLayer().visible()) {
            this.getLayer().redraw();
        }
    }

    async resetLayer() {
        const filter = {};
        if (!filter.properties)
            filter.properties = {};
        await this.setLayerFilters(filter);
        this.getLayer().redraw();
    }

    redraw() {
        $(`#${this.options.name.toLowerCase()}-tab-link`).trigger('click');
        this.getLayer().redraw();
    }

    show(options = {}, features = this.getLayer().getCurrentFeatures()) {
        if ($(`#${this.options.name.toLowerCase()}-collapse`).length === 0 || options.refresh) {
            if (options.empty) {
                this._view(null);
            } else {
                this._view(features, this.options.context);
            }
        }

        if (options.show) {
            infoBoard.open();
            $(`#${this.options.name.toLowerCase()}-tab-link`).trigger('click');
            if (options.id) {
                const title = $(`#${this.options.name.toLowerCase()}-title-${options.id}`);
                return setTimeout(() => {
                    if (title[0] && !title[0].isOpen) {
                        title.trigger('select');
                    }

                    if (options.tab) {
                        $(`#button-${this.options.name.toLowerCase()}-${options.tab}-${options.id}`).trigger('click');
                    }
                }, 300);
            }
        }
    }

    toggleVisibility(id, layer) {
        this._instance.toggleVisibility(id, layer);
    }

    highlight(marker, event) {
        this._instance.highlight(marker, event);
    }

    select(marker, type) {
        this._instance.select(marker, type);
    }

    unselect(marker, type) {
        this._instance.unselect(marker, type);
    }

    _treatDealersWithSameName(sales) {
        sales = sales.map((s, idx) => {
            const dealersSameName = sales.filter(x => x.Dealer === s.Dealer);

            if (dealersSameName.length > 1) {
                s.Dealer = Array.from({ length: idx }).reduce(acc => acc + ' ', s.Dealer);
            }

            return s;
        });
    }
}

export default StoresControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/StoresControl.js